import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR, imgUrl } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";
import { OrderStatusTracker } from "@/components/OrderStatusTracker";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [id]);

  if (!order) return <div className="py-24 text-center text-[#7c7273]">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="order-confirmation-page">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4a7a59]/10 rounded-full mb-6">
        <CheckCircle2 size={32} className="text-[#4a7a59]" />
      </div>
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-2">Order Confirmed</div>
      <h1 className="font-serif text-4xl text-[#2b1b1c]">Thank you, {order.address.full_name.split(" ")[0]}.</h1>
      <p className="font-italic-serif text-xl text-[#7c7273] mt-3">Your treasures are on their way.</p>

      <div className="mt-10 text-left bg-[#f5f3ef] p-8">
        <div className="flex justify-between mb-6 pb-4 border-b border-[#e8e4db]">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#7c7273]">Order</div>
            <div className="font-serif text-xl">#{order.order_id.slice(-8).toUpperCase()}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-[#7c7273]">Status</div>
            <div className="font-serif text-xl text-[#4a7a59] capitalize">{order.status.replace(/_/g, " ")}</div>
          </div>
        </div>

        <div className="mb-6">
          <OrderStatusTracker status={order.status} statusHistory={order.status_history} />
        </div>

        <ul className="space-y-3">
          {order.items.map((it) => (
            <li key={`${it.product_id}::${it.variant_id || "base"}`} className="flex gap-3 items-center">
              <img src={imgUrl(it.image)} alt="" className="w-14 h-16 object-cover" />
              <div className="flex-1">
                <div className="font-serif">{it.name}</div>
                <div className="text-sm text-[#7c7273]">{it.color ? `${it.color} · ` : ""}Qty {it.quantity}</div>
              </div>
              <div>{formatINR(it.line_total)}</div>
            </li>
          ))}
        </ul>
        <div className="h-px bg-[#e8e4db] my-4" />
        <div className="text-sm space-y-1">
          <div className="flex justify-between"><span className="text-[#7c7273]">Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-[#7c7273]">Shipping</span><span>{order.shipping === 0 ? "Free" : formatINR(order.shipping)}</span></div>
          <div className="flex justify-between"><span className="text-[#7c7273]">Tax</span><span>{formatINR(order.tax)}</span></div>
          <div className="flex justify-between font-serif text-lg pt-2 border-t border-[#e8e4db]"><span>Total</span><span className="text-[#7a1e2b]">{formatINR(order.total)}</span></div>
        </div>
        <div className="mt-6 text-sm">
          <div className="text-xs uppercase tracking-widest text-[#7c7273] mb-1">Shipping to</div>
          <div>{order.address.full_name}, {order.address.line1}, {order.address.city}, {order.address.state} - {order.address.pincode}</div>
        </div>
        {order.demo_mode && (
          <div className="mt-4 text-xs text-[#c9a34a] bg-white border border-[#c9a34a]/40 p-3">
            DEMO MODE: This order was placed with placeholder Razorpay keys. Add real keys to enable live checkout.
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-center gap-3">
        <Link to="/shop" className="btn-outline" data-testid="order-continue-shopping">Continue Shopping</Link>
        <Link to="/account?tab=orders" className="btn-primary" data-testid="order-view-orders">View Orders</Link>
      </div>
    </div>
  );
}
